import "@tento-ui/components/sheet/sheet-layout";
import "./sheet-legend.ts";
import "./legend-sentinel.ts";
import { LegendSentinel } from "./legend-sentinel.ts";
import { type Option, type Unsubscribe } from "@tento-lona/sheets";
import { StructuralCoHide } from "@tento-lona/sheets/platform";
import { RowLocalId, RowNetworkKey } from "@tento-lona/sheets";
import type { RowSyncState } from "@tento-lona/sheets";
import { type SheetRow, SheetViewModel } from "@tento-lona";
import { DateTime, Time, Utc } from "@tento-chrono";
import { nonnull } from "@tento-core/assert";
import { LiveData } from "@tento-core/livedata";
import { Tracing } from "@tento-core/tracing";
import { html, LonaWebComponent } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { Style } from "@tento-ui/component-styles";
import { CellHeight } from "@tento-ui/components/sheet/cell-height";
import { ColumnIndex } from "@tento-ui/components/sheet/column-index";
import { SheetCell } from "@tento-ui/components/sheet/sheet-cell";
import { SheetLayout, SheetLayoutPort } from "@tento-ui/components/sheet/sheet-layout";
import { SheetLayoutWidth } from "@tento-ui/components/sheet/sheet-layout-width";
import { $$ } from "@tento-ui/fastdom";
import { BindGeneration } from "@tento-scroll/engine/scroll-engine";
import type { ScrollEngine } from "@tento-scroll/engine/scroll-engine";
import { SizeGate } from "@tento-ui/ui/size-gate";
import { SheetLayoutGate } from "./sheet-layout-gate.ts";
import {
  SHEET_REVEAL_INSTRUMENTATION_KEY,
  SHEET_LAYOUT_INSTRUMENTATION_KEY,
  type SheetRevealInstrumentation,
  type SheetLayoutInstrumentation,
} from "../instrumentation.ts";
import { RowViewport } from "@tento-lona/sheets/viewport";
import { type RowResidency, RowResidencyOwner } from "./binder/row-residency.ts";
import { makeFixedChildHandle } from "./sheet-fixed-child-handle.ts";
import { bindSheetWork } from "./sheet-bind-work.ts";
import { SheetCellTargets } from "./sheet-cell-targets.ts";
import { SheetGeometryRebinds } from "./sheet-geometry-rebinds.ts";
import { sheetElementStyles } from "./sheet-element-styles.ts";
import { makeRebindHandle, RebindHandleState } from "./sheet-rebind-handle.ts";
import { EmitHandleState, makeEmitHandle, type EmitResidency } from "./sheet-emit-handle.ts";
import { makeStateHandle } from "./sheet-state-handle.ts";
import { makePopoverHandle } from "./sheet-popover-handle.ts";
import { makeStaticChildHandle } from "./sheet-static-child-handle.ts";
import { ColumnRetentionGate } from "./column-retention.ts";
import { BindReasonLedger, membershipPassesEnabled } from "./bind-reasons.ts";
import { DefaultBinder } from "./binder/default-binder.ts";
import { SheetPresentation } from "./sheet-presentation.ts";
import { SheetDecorations } from "./decorations/sheet-decorations.ts";
import { RenderPluginManager, RowOptionsElement } from "./render-plugin.ts";
import { RowOptionsAnchor } from "./row-options-anchor.ts";
import { SheetColumn } from "./sheet-column.ts";
import { ColumnPlaceholder } from "./column-placeholder.ts";
import { ScrollHorizontal } from "@tento-ui/components/scroll/scroll-horizontal";
import { SheetDelegate } from "./sheet-delegate.ts";
import { SheetBinder, SheetIndexRenderer } from "./sheet-index-renderer.ts";
import { PretextLegendMeasurer } from "./legend-pretext-measurer.ts";
import { SheetLegend } from "./sheet-legend.ts";
import type { SheetNavigator } from "@tento-ui/components/sheet/sheet-navigator";
import type { Size } from "@tento-ui/layout-utils";
import { Internals } from "@tento-lona/sheets/internal-ui";
import { harnessLayoutInstrumentation, harnessRevealInstrumentation, type HarnessHost } from "./sheet-element-harness.ts";
import { applySizeChange, seedSizeFromMeasurement, type SizeHost, SheetSizeState } from "./sheet-element-size.ts";
import { FixedChildrenQueue } from "./fixed-children-queue.ts";
import { SheetFrameScrollTop } from "./sheet-frame-scroll-top.ts";
import { FixedChildrenLifetime } from "./fixed-children-lifetime.ts";
import { SheetElementLayout } from "./sheet-element-layout.ts";
import { makeTooltipHandle, type PopoverHandleHost, popoverHostOf } from "./sheet-popover-handle.ts";
import { decorationRendererContext } from "./sheet-decoration-context.ts";
import { type LegendHostingHost, LegendHostingState, makeLegendHandle, rebindLegend } from "./sheet-legend-hosting.ts";
import type * as LegendHosting from "./sheet-legend-hosting.ts";
import {
  assertrl,
  bindSheetWorkHostOf,
  cellTargetsHostOf,
  decorationHostOf,
  delegateHandleOf,
  emitHostOf,
  fixedChildHostOf,
  geometryHostOf,
  layoutDebugOverlayHandleOf,
  legendHostOf,
  rebindHostOf,
  type SheetElementParts,
  stateHostOf,
  staticChildHostOf,
} from "./sheet-element-hosts.ts";
import { bindColumn, bindSheetElement, cancelBind } from "./sheet-element-bind.ts";
import {
  applyNavigationState,
  cancelNavigation,
  listenReveal,
  presentationHost,
  presentationReady,
  recordSettle,
} from "./sheet-element-presentation.ts";
import type * as Presentation from "./sheet-element-presentation.ts";
import { beginViewModelSwap } from "./sheet-element-swap.ts";
import type { BindSheetOptions } from "./sheet-bind-options.ts";
import type { SheetElementCore } from "./sheet-element-core.ts";
import { CommittedRowGeometry, SheetBindState, SheetPresentationState, ViewModelSwapState } from "./sheet-element-state.ts";
import { SheetRowViewport } from "./sheet-row-viewport.ts";

/** `SheetElement`'s friend record (`Internals`): its constructor registers each instance's harness host. */
const sheetElementInternals = new Internals<SheetElement, HarnessHost>("SheetElement");

/**
 * The test-harness seam onto a `std-sheet` element (see `SheetLayoutInstrumentation`).
 *
 * @internal Not on the `@tento/lona/sheets-ui` barrel; tests import
 * `sheetLayoutInstrumentationOf` from the workspace-only
 * `@tento-lona/sheets-ui/internal-testing`.
 */
export function sheetLayoutInstrumentationOf(sheet: SheetElement): SheetLayoutInstrumentation {
  return harnessLayoutInstrumentation(sheetElementInternals.of(sheet));
}

/**
 * The test-harness seam onto a `std-sheet` element's reveal-contract
 * internals (see `SheetRevealInstrumentation`).
 *
 * @internal Not on the `@tento/lona/sheets-ui` barrel; tests import
 * `sheetRevealInstrumentationOf` from the workspace-only
 * `@tento-lona/sheets-ui/internal-testing`.
 */
export function sheetRevealInstrumentationOf(sheet: SheetElement): SheetRevealInstrumentation {
  return harnessRevealInstrumentation(sheetElementInternals.of(sheet));
}

export { sheetRevealReadyOf } from "./sheet-element-harness.ts";

/**
 * Install the `Symbol.for` harness door onto `SheetElement.prototype`
 * (idempotent; opt-in through `installUiHarnessDoors`,
 * `@tento-lona/sheets-ui/internal-testing`).
 */
export function installSheetLayoutInstrumentationDoor(): void {
  const proto = SheetElement.prototype;
  if (Object.prototype.hasOwnProperty.call(proto, SHEET_LAYOUT_INSTRUMENTATION_KEY)) return;
  Object.defineProperty(proto, SHEET_LAYOUT_INSTRUMENTATION_KEY, {
    value: function sheetLayoutInstrumentationDoor(this: SheetElement): SheetLayoutInstrumentation {
      return sheetLayoutInstrumentationOf(this);
    },
  });
}

/**
 * Install the `Symbol.for` harness door onto `SheetElement.prototype`
 * (idempotent; opt-in through `installUiHarnessDoors`,
 * `@tento-lona/sheets-ui/internal-testing`).
 */
export function installSheetRevealInstrumentationDoor(): void {
  const proto = SheetElement.prototype;
  if (Object.prototype.hasOwnProperty.call(proto, SHEET_REVEAL_INSTRUMENTATION_KEY)) return;
  Object.defineProperty(proto, SHEET_REVEAL_INSTRUMENTATION_KEY, {
    value: function sheetRevealInstrumentationDoor(this: SheetElement): SheetRevealInstrumentation {
      return sheetRevealInstrumentationOf(this);
    },
  });
}




export type { BindSheetOptions } from "./sheet-bind-options.ts";



/**
 * The `std-sheet` element: renders a sheet's view model as a scrolling grid
 * of rows and columns.
 */
@component({
  name: "std-sheet",
})
export class SheetElement extends LonaWebComponent {
  #$$ = {
    root: this.hydrate<SheetLayout>("root"),
    legend: this.hydrate<SheetLegend>("sheet-legend"),
    headerLegend: this.hydrate<SheetLegend>("sheet-header-legend"),
    footerLegend: this.hydrate<SheetLegend>("sheet-footer-legend"),
    navigationLoadingOverlay: this.$("navigation-loading-overlay"),
    ariaLiveRegion: this.$("aria-live-region"),
  };

  // @state[init]
  #pluginManager: Option<RenderPluginManager> = null;
  #shouldShowDebugPopover: Option<LiveData.Readable<boolean>> = null;
  /**
   * App-injected renderer for the COMPOSED options surface. When set,
   * `revealRowOptionsPopover` composes the visible row's options model and —
   * if non-empty — renders it through this instead of the row's plugin
   * `makeRowOptions` factory. Absent ⇒ legacy per-plugin factory path only.
   */
  #composedOptionsRenderer: Option<RowOptionsElement.ComposedRenderer> = null;
  /**
   * The layout surface this sheet drives — see {@link SheetLayoutPort}. Read
   * through the element's `port` getter rather than held as a field so a
   * recycled host cannot strand a stale one; the port itself is stable for the
   * element's lifetime.
   */
  get #layout(): SheetLayoutPort {
    return this.#$$.root.port;
  }
  get navigator(): Option<SheetNavigator> {
    // The one reach-through this rung leaves standing: `SheetNavigator` is a
    // whole object the APP layer drives (`$sheet.navigator?.jumpTo(...)`), so
    // naming it on the port would publish the navigator's own surface as part
    // of the layout's. It earns its own face when the app layer's uses are
    // enumerated, not here.
    return this.#$$.root.navigator ?? null;
  }

  // @state[sheet]
  sheet: Option<SheetViewModel.Surface<SheetBinder>> = null;
  editable: boolean = false;
  /** Debug: emits pretext measurement (and optionally sentinel comparison). */
  readonly legendMeasureDebug = new LiveData<
    Option<SheetElement.LegendMeasureComparison>
  >(null);
  /** Debug flags — set from app layer, read during sheet rebinds. */
  columnRebindToast: Option<LiveData.Readable<boolean>> = null;
  legendMeasureToast: Option<LiveData.Readable<boolean>> = null;
  legendMeasureSentinel: Option<LiveData.Readable<boolean>> = null;
  #activeRowOptions = new LiveData<Option<RowLocalId>>(null);
  #currentTime: LiveData<DateTime<Utc>> = new LiveData(Utc.now());
  #currentTimeInterval: Option<ReturnType<typeof setInterval>>;
  readonly #swapSettleCleanups = new Set<() => void>();
  /**
   * The step state, one object per owning module (sheet-element-state.ts):
   * the element creates them and hands them to its steps; it writes none of
   * their fields itself.
   */
  readonly #bindState = new SheetBindState();
  readonly #presentationState = new SheetPresentationState();
  readonly #swapState = new ViewModelSwapState();
  readonly #committed = new CommittedRowGeometry();
  readonly #sizeState = new SheetSizeState();
  readonly #legendState = new LegendHostingState();
  readonly #rebindState = new RebindHandleState();
  readonly #revealListeners = new Set<(reveal: SheetElement.Reveal) => void>();

  /**
   * The element as its step modules read it (sheet-element-core.ts): the one
   * place its sheet, layout, bind state, presentation and committed geometry
   * are forwarded. The presentation is read lazily: its host is built from
   * the table below, which carries this core.
   */
  readonly #core: SheetElementCore = ((self): SheetElementCore => ({
    element: self,
    get sheet() { return self.sheet; },
    get layout() { return self.#layout; },
    bindState: self.#bindState,
    get presentation() { return self.#presentation; },
    committedRowGeometry: self.#committed,
  }))(this);

  /** Retires in-flight fixed-children requests on detach, recycle and rebind (fixed-children-lifetime.ts). */
  readonly #fixedChildrenLifetime = new FixedChildrenLifetime();

  /**
   * The element's one table (sheet-element-hosts.ts): what its bind,
   * presentation and swap steps work on (sheet-element-port.ts), plus the
   * private parts every other step host is built from. Collaborators and
   * operations, no setters; read when a step runs.
   */
  readonly #port: SheetElementParts = ((self): SheetElementParts => ({
    core: self.#core,
    bindState: self.#bindState,
    presentationState: self.#presentationState,
    swapState: self.#swapState,
    legendState: self.#legendState,
    get rows() { return self.#rows; },
    adoptSheet: (sheet) => {
      self.sheet = sheet;
      self.editable = sheet?.editable ?? false;
    },
    forgetVisibleColumns: () => {
      self.#emitState.forgetVisibleColumns();
    },
    recordSettle: (via) => recordSettle(self.#presentationState, via),
    get $$() { return self.#$$; },
    get activeRowOptions() { return self.#activeRowOptions; },
    forgetStaticChildren: () => {
      self.#emitState.forgetStaticChildren();
    },
    get bindReasons() { return self.#bindReasons; },
    get defaultBinder() { return self.#defaultBinder; },
    get emitHandle() { return self.#emitHandle; },
    get fixedChildrenLifetime() { return self.#fixedChildrenLifetime; },
    get layoutGate() { return self.#layoutGate; },
    forgetSpecGenerations: () => {
      self.#specGeneration.clear();
    },
    trackSwapSettle: (cancel) => {
      self.#swapSettleCleanups.add(cancel);
    },
    untrackSwapSettle: (cancel) => {
      self.#swapSettleCleanups.delete(cancel);
    },
    get columnRetention() { return self.#columnRetention; },
    recordColumnScene: ($column, column) => {
      self.#columnScenes.set($column, column);
    },
    get currentTime() { return self.#currentTime; },
    get rightOccludedWidthPx() { return self.#_rightOccludedWidthPx; },
    get size() { return self.size; },
    get layoutDebugOverlayHandle() { return self.layoutDebugOverlayHandle; },
    get decorationsHandle() { return self.decorationsHandle; },
    get legendHandle() { return self.legendHandle; },
    get rebindHandle() { return self.rebindHandle; },
    updateAriaGridCounts: (sheet) => self.#updateAriaGridCounts(sheet),
    bindSheetWork: (sheet, options) => self.#bindSheetWork(sheet, options),
    rebindCellHeights: () => self.rebindCellHeights(),
    rebindStaticChildPositions: () => self.rebindStaticChildPositions(),
    rebindFixedChildYPositions: () => self.rebindFixedChildYPositions(),
    element: self,
    rebindState: self.#rebindState,
    get emitState() { return self.#emitState; },
    committed: self.#committed,
    get cellTargets() { return self.#cellTargets; },
    get sizeGate() { return self.#sizeGate; },
    get lockedIndex() { return self.#lockedIndex; },
    get pluginManager() { return self.#pluginManager; },
    get shouldShowDebugPopover() { return self.#shouldShowDebugPopover; },
    get composedOptionsRenderer() { return self.#composedOptionsRenderer; },
    get syncStateProvider() { return self.#syncStateProvider; },
    get legends() { return { legend: self.#$$.legend, headerLegend: self.#$$.headerLegend, footerLegend: self.#$$.footerLegend }; },
    get legendHost() { return self.#legendHost; },
    get fixedChildrenQueue() { return self.#fixedChildrenQueue; },
    defaultCollapsedLegendPx: SheetElement.DEFAULT_COLLAPSED_LEGEND_PX,
    nextSpecGeneration: (key) => {
      const gen = (self.#specGeneration.get(key) ?? 0) + 1;
      self.#specGeneration.set(key, gen);
      return gen;
    },
    isCurrentSpecGeneration: (key, gen) => self.#specGeneration.get(key) === gen,
    applyResolvedLayout: (sheet, layout, props, content) => self.#applyResolvedLayout(sheet, layout, props, content),
    seedSizeFromMeasurement: () => self.#seedSizeFromMeasurement(),
  }))(this);

  /** The presentation machine, hosted by the table (sheet-element-presentation.ts `presentationHost`). */
  readonly #presentation = new SheetPresentation(presentationHost(this.#port, this.#revealListeners));

  #cancelSettleConsumers(): void {
    this.#presentation.disconnect();
    cancelNavigation(this.#presentationState);
    cancelBind(this.#bindState);
    for (const cleanup of [...this.#swapSettleCleanups]) cleanup();
  }

  onRecycle(): void {
    this.#bindReasons.reset();
    this.#rows.reset();
    this.#defaultBinder.dispose?.();
    this.#fixedChildrenLifetime.retire();
    this.#fixedChildrenQueue.clear();
    this.#cancelSettleConsumers();
    this.#presentation.deferInitialReveal(null);
    this.#layoutGate.reset();
    super.onRecycle();
  }
  /** Fixed-child request generation per row and owner; written here only, reached by the port and emit host through methods. */
  readonly #specGeneration: Map<string, number> = new Map();
  /** Tracks which static child IDs are currently revealed per row (by rowId string). */

  // @state[sync]
  #syncStateProvider: Option<(rowId: RowLocalId) => Option<RowSyncState>> =
    null;

  /**
   * Configure the sync state provider for row sync indicators.
   * @param provider Function that returns sync state for a given row ID
   */
  setSyncStateProvider(
    provider: Option<(rowId: RowLocalId) => Option<RowSyncState>>,
  ): void {
    this.#syncStateProvider = provider;
  }

  #resolvedCellWidthPx: Option<number> = null;
  get resolvedCellWidthPx(): Option<number> {
    return this.#resolvedCellWidthPx;
  }
  #resolvedCellWidth = new LiveData<Option<SheetLayout.Width>>(null);
  get resolvedCellWidth(): LiveData.Readable<Option<SheetLayout.Width>> {
    return this.#resolvedCellWidth;
  }

  // @state[layout]
  #layoutGate = new SheetLayoutGate();
  readonly leftOccludedWidthPx: LiveData<number> = new LiveData(0);

  /** Delay resize-only layout work; a full bind still consumes current geometry. */
  holdLayoutUpdates(): () => void {
    return this.#layoutGate.holdUpdates();
  }

  #_rightOccludedWidthPx: number = 0;
  set rightOccludedWidthPx(n: number) {
    if (this.#_rightOccludedWidthPx === n) return;
    this.#_rightOccludedWidthPx = n;
    this.#layoutGate.setRightOccludedPx(n);
  }
  get rightOccludedWidthPx(): number {
    return this.#_rightOccludedWidthPx;
  }
  /**
   * Pins the legend rail to an exact width instead of the measured one.
   * `null` (the default) keeps the measured width with its 150px floor;
   * a number is used VERBATIM — it bypasses the floor, so an embedder that
   * wants a narrow (or extra-wide) rail gets exactly what it asked for.
   * Read on the next legend rebind, so set it before binding a sheet.
   */
  legendWidthOverridePx: Option<number> = null;

  /** Rail width used while collapsed when `legendCollapsedWidthPx` is null. */
  static readonly DEFAULT_COLLAPSED_LEGEND_PX = 28;

  /**
   * Width of the COLLAPSED rail, in px. `null` (the default) uses
   * `DEFAULT_COLLAPSED_LEGEND_PX`; a number is taken VERBATIM, and `0` is
   * legal — an embedder that wants the rail gone entirely while collapsed
   * gets exactly that.
   */
  legendCollapsedWidthPx: Option<number> = null;

  /**
   * Collapse is a BOOLEAN state, deliberately orthogonal to
   * `legendWidthOverridePx` rather than a third value of a width "mode" enum.
   * A mode enum would force every caller to re-declare its expanded policy on
   * every expand (was it overridden? measured?); a boolean lets collapse
   * borrow the rail for a while and hand it straight back — expanding always
   * restores whatever the override/measured policy already says.
   */
  #_legendCollapsed = false;
  get legendCollapsed(): boolean {
    return this.#_legendCollapsed;
  }

  /**
   * Collapse or expand the legend rail. Safe to call before a sheet is bound:
   * the flag is the source of truth and `bindSheet`'s own `rebindLegend` reads
   * it, so an embedder can start collapsed without a flash of full-width rail.
   * With a sheet bound, the re-measure pass re-applies the width and flips the
   * host attribute in one mutate.
   */
  setLegendCollapsed(collapsed: boolean): void {
    if (this.#_legendCollapsed === collapsed) return;
    this.#_legendCollapsed = collapsed;
    if (this.sheet) void this.rebindLegendMeasurement();
  }

  /** The element's last accepted size (sheet-element-size.ts keeps it). */
  get size(): Option<Size> {
    return this.#sizeState.size;
  }
  #sizeGate = new SizeGate();
  readonly #sizeHost: SizeHost = ((self): SizeHost => ({
    core: self.#core,
    state: self.#sizeState,
    get sizeGate() { return self.#sizeGate; },
    get layoutGate() { return self.#layoutGate; },
    refreshRowViewport: () => {
      self.#frameScrollTop.expire();
      self.#rows.refresh();
    },
    get onSizeChange() { return self.onSizeChange; },
  }))(this);
  /** Take a ResizeObserver size (sheet-element-size.ts). */
  onSizeChange: Option<(s: Size) => void> = (s: Size) => applySizeChange(this.#sizeHost, s);

  /** Seed `SizeGate` from a direct measurement when no notification arrived (sheet-element-size.ts). */
  #seedSizeFromMeasurement(): void {
    seedSizeFromMeasurement(this.#sizeHost);
  }

  constructor() {
    super();
    // A decorated class holds no static `#` field (TS18036): its harness host is built here.
    const sheet = this;
    sheetElementInternals.register(this, Object.freeze<HarnessHost>({
      get scroll() { return sheet.#$$.root.$$.scroll; },
      get layout() { return sheet.#layout; },
      get defaultBinder() { return sheet.#defaultBinder; },
      presentationReady: () => presentationReady(sheet.#port),
      get presentationState() { return sheet.#presentationState; },
      adoptSheet: (surface) => { sheet.sheet = surface as SheetViewModel.Surface<SheetBinder>; },
    }));
    this.#$$.headerLegend.isFrozen = true;
    this.#$$.footerLegend.isFooter = true;

    this.#currentTimeInterval = setInterval(() => {
      this.#currentTime.update(Utc.now());
    }, 2 * Time.MS_PER_MIN);
  }

  disconnectedCallback() {
    this.#bindReasons.reset();
    this.#rows.reset();
    this.#cancelSettleConsumers();
    this.#fixedChildrenLifetime.retire();
    this.#fixedChildrenQueue.clear();
    super.disconnectedCallback();
    if (this.#currentTimeInterval != null) {
      clearInterval(this.#currentTimeInterval);
      this.#currentTimeInterval = null;
    }
    // Disconnect is the only moment this element lets go of `defaultBinder` —
    // the field is `readonly` and minted once per element, so there is no other
    // drop point — and a detached sheet paints nothing, which makes holding its
    // render surface across the detach exactly the seam-17 leak. Symmetric with
    // the interval above: released here, re-armed lazily (the binder re-acquires
    // at the top of its next `onBind`/`rebind`, so a reparent or a pool recycle
    // needs no `connectedCallback` counterpart).
    this.#defaultBinder.dispose?.();
  }

  connectedCallback() {
    super.connectedCallback();
    this.#presentation.reconnect();
    if (this.#currentTimeInterval == null) {
      this.#currentTimeInterval = setInterval(() => {
        this.#currentTime.update(Utc.now());
      }, 2 * Time.MS_PER_MIN);
    }
    // Tracing.dev("2025-09-23T23:41:00.683Z", this.currentTime.toString());
    // if (true) {
    //   this.currentTime = Utc.parse("2025-09-23T19:41:00.683Z")!;
    // }
  }

  /**
   * Inject the host's composed-options renderer (maps the DOM-free model →
   * widgets + re-render dispatch). The reveal path tries composition first and
   * uses this when the row declares contributions; falls back to the legacy
   * per-plugin factory otherwise. Set once after `init`.
   */
  setComposedOptionsRenderer(renderer: RowOptionsElement.ComposedRenderer): void {
    this.#composedOptionsRenderer = renderer;
  }

  /** Why each column's next full bind runs (F10). */
  readonly #bindReasons = new BindReasonLedger(membershipPassesEnabled());
  /** Cell and fixed/static-child target resolution (F11). */
  readonly #cellTargets = new SheetCellTargets(cellTargetsHostOf(this.#port));
  /** What the geometry rebinds read (F11; `sheet-geometry-rebinds.ts`). */
  readonly #geometryHost = geometryHostOf(this.#port);
  /** Column-scene retention, at most once per frame (F14). */
  readonly #columnRetention = new ColumnRetentionGate();
  /** The residency this element's viewport and default binder share (one build per sheet geometry). */
  readonly #residencyOwner = RowResidencyOwner.create();
  readonly #columnScenes = new WeakMap<SheetColumn, ColumnIndex.Key.Resolved.Default>();
  /** The row viewport: focus, membership and residency (sheet-row-viewport.ts). */
  readonly #rows: SheetRowViewport = new SheetRowViewport(((self): SheetRowViewport.Host => ({
    get sheet() { return self.sheet; },
    get size() { return self.size; },
    residencyOwner: self.#residencyOwner,
    scrollTop: () => self.#scrollTopForFrame(),
    membershipChanged: () => {
      if (self.sheet == null || !self.isConnected) return;
      self.#bindReasons.membershipChanged();
      self.#$$.root.scrollPort.emitAll(false, "row-viewport-membership");
      // Rows the new residency demands get the rendered emit's per-row pass.
      self.#emitHandle.demandChanged();
    },
  }))(this));

  /** The scroll position once per frame, from the scroll event's report when there is one (every arm). */
  readonly #frameScrollTop = new SheetFrameScrollTop({
    port: () => this.#$$.root.scrollPort,
    frameNumber: () => $$.frameNumber(),
    onDomRead: (frame) => Tracing.log("sheet/scroll-top/dom-read", { frame, phase: $$.currentPhase() }),
  });

  #scrollTopForFrame(): Option<number> {
    return this.#frameScrollTop.read();
  }

  /** Per-row fixed-children applies under the frame budget, nearest the row viewport first. */
  readonly #fixedChildrenQueue = FixedChildrenQueue.forDocument(() => {
    const sheet = this.sheet;
    const input = this.#rows.input();
    return (unit) => {
      const row = sheet?.layout.rowLayout.get(unit.rowId);
      if (row == null) return Number.MAX_SAFE_INTEGER;
      if (input.mode !== "window") return 0;
      return FixedChildrenQueue.viewportDistancePx(
        row.offsetPx,
        row.offsetPx + row.cellHeightWithChildrenPx,
        input.topPx,
        input.topPx + input.heightPx,
      );
    };
  });

  #focusRow(cell: SheetCell.CellId): void {
    this.#frameScrollTop.expire();
    this.#rows.focus(cell);
    this.onFocusCell?.(cell);
  }

  #blurRow(cell: SheetCell.CellId): void {
    this.#frameScrollTop.expire();
    this.#rows.blur(cell);
    this.onBlurCell?.(cell);
  }

  init(
    pluginManager: RenderPluginManager,
    shouldShowDebugPopover: Option<LiveData.Readable<boolean>>,
  ) {
    this.#pluginManager = pluginManager;
    this.#shouldShowDebugPopover = shouldShowDebugPopover;
    this.#layout.initialize({
      createColumn: () => SheetColumn.make(),
      recycleColumn: (c) => {
        if (c instanceof SheetColumn) {
          const column = this.#columnScenes.get(c);
          this.#columnScenes.delete(c);
          if (column != null) this.#defaultBinder.retireColumn?.(column);
          c.onRecycle(ScrollHorizontal.columnRetention() > 0 ? "park" : "reset");
        }
      },
      onPlace: (index, item) => {
        if (this.sheet == null) return;
        ColumnPlaceholder.labelHeader(this.sheet, ColumnIndex.Key.fromIndex(index), item.$header);
      },
      onBind: (index, item, widthPx, resizeOnly, isOffscreen) => bindColumn(this.#port, index, item, widthPx, resizeOnly, isOffscreen),
      onViewportColumnsChanged: this.#emitHandle.visible.bind(this),
      onRenderedColumnsWillChange: this.#emitHandle.rendered.bind(this),
      // Genuinely absent: nothing here listens for the rendered strip AFTER it
      // changes — `onRenderedColumnsWillChange` above is where the prefetch and
      // the plugin pass run. Stated as null rather than left unwritten so the
      // absence is a decision a reader can see.
      onRenderedColumnsChanged: null,
      cellWidthForIndex: (index: number): Option<number> => {
        if (this.sheet == null) return assertrl() ?? null;
        return this.sheet.indexRenderer.widthForIndex(index);
      },
      onScroll: null,
      onScrollPosition: (position) => {
        this.#frameScrollTop.moved();
        this.#rows.refresh(position.top);
        const anchor = this.#rebindState.rowOptionsAnchor;
        if (anchor == null) return;
        const info = nonnull(this.#$$.root.scrollPort.getScrollInfo(), "Sheet: scroll port not measured");
        anchor.didViewportScroll(
          info.top,
          info.scrollHeight,
          nonnull(this.size, "Sheet: size not measured").height,
        );
      },
    });
  }

  /*
   Bind
   =============================================================================
  */

  /** Bind `sheet` (or the empty state): see `bindSheetElement` (sheet-element-bind.ts). */
  bind(sheet: Option<SheetViewModel.Surface<SheetBinder>>) {
    bindSheetElement(this.#port, sheet);
  }

  /** Conceal the initial surface until the host chooses its first sheet or empty view. */
  deferInitialReveal(readiness: LiveData.Readable<boolean>): void {
    this.#presentation.deferInitialReveal(readiness);
  }

  /** Observe each "ready" presentation (`SheetElement.Reveal`); only a "verified" one is evidence of a successful render. */
  onReveal(listener: (reveal: SheetElement.Reveal) => void): Unsubscribe {
    return listenReveal(this.#revealListeners, listener);
  }

  /** Apply a navigation's UI state: see sheet-element-presentation.ts. */
  applyNavigationState(state: SheetElement.NavigationUiState) {
    applyNavigationState(this.#port, state);
  }

  readonly #bindSheetWorkHost = bindSheetWorkHostOf(this.#port);

  async #bindSheetWork(
    sheet: SheetViewModel.Surface<SheetBinder>,
    options: BindSheetOptions,
  ) {
    return bindSheetWork(this.#bindSheetWorkHost, sheet, options);
  }

  /** Resolve the uniform cell width from the layout-gate props and bind it. */
  #applyResolvedLayout(
    sheet: SheetViewModel.Surface<SheetBinder>,
    layout: SheetLayoutPort,
    p: SheetLayoutGate.Props,
    content: Option<SheetLayoutPort.Content>,
  ): void {
    Tracing.info("ui")("core/sheet/bind/layout", p);
    const resolved = SheetLayout.resolveCellWidth(
      Math.max(0, p.viewportWidth - p.legendWidth - p.rightOccludedPx),
      sheet.indexRenderer.initialWidth,
      sheet.indexRenderer.initialConstraints,
    );
    this.#resolvedCellWidthPx = resolved?.real ?? null;
    this.#resolvedCellWidth.update(resolved?.semantic ?? null);
    this.leftOccludedWidthPx.update(p.legendWidth);
    const geometry = {
      cellWidthPx: resolved?.real ?? 80,
      legendWidthPx: p.legendWidth,
      rightInsetPx: p.rightOccludedPx,
      viewportWidthPx: p.viewportWidth,
    };
    if (content) layout.bindFrame(geometry, content, sheet.indexRenderer.mapper);
    else layout.bindGeometry(geometry, sheet.indexRenderer.mapper);
  }

  /** Re-run the resolve+bind by re-emitting the gate's current resolved props so
   *  the `applyLayout` onChange closure re-runs against fresh viewmodel state; a
   *  no-op while the gate is unresolved or mid-rebind (onChange null). Driven by
   *  the `cell-width` invalidation effect on occlusion/count change. */
  rebindCellWidth(): void {
    this.#layoutGate.refresh();
  }

  rebindCellHeights() {
    SheetGeometryRebinds.rebindCellHeights(this.#geometryHost);
  }

  rebindStaticChildPositions() {
    SheetGeometryRebinds.rebindStaticChildPositions(this.#geometryHost);
  }

  rebindFixedChildYPositions() {
    SheetGeometryRebinds.rebindFixedChildYPositions(this.#geometryHost);
  }

  rebindFixedChildPositions() {
    this.#layout.rebindFixedChildPositions();
    this.rebindFixedChildYPositions();
  }

  rebindBodyMarginTop() {
    if (!this.sheet) return;
    this.#layout.presentation.setBodyMarginTop(
      SheetElement.Layout.bodyMarginTop(this.sheet),
    );
  }

  rebindScrollHeight() {
    const sheet = this.sheet;
    if (!sheet) return;
    this.#layout.bindScrollHeightOnly(sheet.layout.bodyHeightPx);
  }

  rebindFooterGeometry() {
    if (!this.sheet) return;
    this.#layout.presentation.setFooterHeight(
      this.sheet.layout.footerRowsHeightPx + 2,
    );
    const hasContentFooterRows = this.sheet.joinedTree.flattened.rows.some(
      (r) => r.surface === "footer" && r.descriptor.id !== "sheet-footer",
    );
    this.#$$.root.scrollPort.toggleFooterBackground(hasContentFooterRows);
  }

  /** Re-measure legend width and recalculate cell widths. */
  async rebindLegendMeasurement() {
    if (!this.sheet) return;
    const width = await rebindLegend(this.#legendHost, false);
    this.#layoutGate.setLegendWidth(width);
  }

  /** Notify plugins that rows have been (re)bound. */
  fireOnBindRow() {
    if (this.sheet) this.#emitHandle.onBindRow(this.sheet);
  }

  readonly #legendHost: LegendHostingHost = legendHostOf(this.#port);

  rebindLegendStickyTop() {
    this.#$$.legend.style.setProperty(
      "--sticky-top",
      String(this.sheet ? SheetElement.Layout.legendStickyTop("scrolling", this.sheet) : 0),
    );
    this.#$$.headerLegend.style.setProperty("--sticky-top", "");
  }

  /**
   * Show or hide the sheet's content under its header (`content`), and
   * whether the header casts its shadow over what is below it (`headerShadow`).
   */
  bindContentVisibility(visibility: SheetElement.ContentVisibility) {
    const shown = visibility.content === "shown";
    this.#$$.root.scrollPort.toggleContentVisibility(shown && !this.#presentation.concealing, true);
    this.#$$.root.scrollPort.headerPeek.toggleFullScreen(!shown, true);
    this.#$$.root.scrollPort.headerPeek.toggleShadow(visibility.headerShadow === "shown");
  }

  /*
   Accessibility (ARIA)
   =============================================================================
  */

  /**
   * Update ARIA row and column counts for screen readers.
   * Called when the sheet is bound or when structure changes.
   */
  #updateAriaGridCounts(sheet: SheetViewModel.Surface<SheetBinder>): void {
    const rowCount = sheet.joinedTree.flattened.rows.length;
    // For a virtualized date-based grid with infinite scrolling,
    // we use -1 to indicate an unknown column count per ARIA spec
    const colCount = -1;

    $$.mutate(() => {
      this.#layout.presentation.setGridSize(rowCount, colCount);
    });
  }

  /**
   * Announce a message to screen readers via the live region.
   * Use for status updates like viewport changes or cell edits.
   */
  announceToScreenReader(message: string): void {
    $$.mutate(() => {
      // Clear and re-set to ensure the message is announced even if identical
      this.#$$.ariaLiveRegion.textContent = "";
      // Use setTimeout to ensure the clear is processed before the new message
      setTimeout(() => {
        this.#$$.ariaLiveRegion.textContent = message;
      }, 50);
    });
  }

  /*
   Helpers
   =============================================================================
  */


  /*
   Handles
   =============================================================================
  */

  // Delegate
  onFocusCell: Option<(cell: SheetCell.CellId) => void> = null;
  onBlurCell: Option<(cell: SheetCell.CellId) => void> = null;
  onViewportChanged: Option<
    (
      columns: ColumnIndex.Key.Resolved.Default.Range,
      viewportChangeSource: ScrollEngine.ViewportChangeSource,
    ) => void
  > = null;
  onTryResize: Option<(row: SheetRow.Joined.Handle, n: number) => number> = null;

  // Index Lock
  #lockedIndex: Option<number> = null;
  lockCurrentIndex(): void {
    this.#lockedIndex = this.#$$.root.scrollPort.visibleStartIndex();
  }
  unlockIndex(): void {
    this.#lockedIndex = null;
  }

  // Deferred emit-loop state (`EmitHandleState`, owned by the emit handle): the `visible`/`rendered` bodies are O(rows)
  // plugin loops (plus a decorations rebind that measures AND writes) and used
  // to run synchronously inside the scroll transaction — 4-13ms per column
  // crossing on the wheel path. They now run in the next fastdom read phase,
  // latest-columns-wins, with a bind-generation guard so a superseded sheet's
  // deferred emit never runs (the successor's own bind re-emits). The fetch
  // kick in `rendered` stays synchronous — prefetch latency is user-visible.
  readonly #emitState = new EmitHandleState();
  readonly #emitHandle = makeEmitHandle(emitHostOf(this.#port));

  readonly layoutDebugOverlayHandle: SheetElement.LayoutDebugOverlayHandle = layoutDebugOverlayHandleOf(this.#port);

  readonly legendHandle: SheetElement.LegendHandle = makeLegendHandle(this.#legendHost);

  readonly decorationsHandle = new SheetDecorations(() => decorationRendererContext(decorationHostOf(this.#port)));

  /** See `beginViewModelSwap` in sheet-element-swap.ts. */
  beginViewModelSwap(): () => void {
    return beginViewModelSwap(this.#port);
  }

  readonly rebindHandle: SheetElement.RebindHandle = makeRebindHandle(rebindHostOf(this.#port));

  readonly #popoverHost: PopoverHandleHost = popoverHostOf(this.#core);

  readonly tooltipHandle: SheetElement.TooltipHandle = makeTooltipHandle(this.#popoverHost);

  readonly popoverHandle: SheetElement.PopoverHandle = makePopoverHandle(this.#popoverHost);

  readonly fixedChildHandle: SheetElement.FixedChildHandle = makeFixedChildHandle(fixedChildHostOf(this.#port));

  readonly staticChildHandle: SheetElement.StaticChildHandle = makeStaticChildHandle(staticChildHostOf(this.#port));

  readonly stateHandle: SheetDelegate.State = makeStateHandle(stateHostOf(this.#port));

  readonly handle: SheetElement.Handle = delegateHandleOf(this.#port);

  readonly #defaultBinder: SheetBinder = new DefaultBinder(
    this.handle,
    (cell) => this.#focusRow(cell),
    (cell) => this.#blurRow(cell),
    this.#currentTime,
    this.#residencyOwner,
  );

  static $styles: Style.Compat[] = sheetElementStyles();

  static $html: Option<HTMLTemplateElement> = html`
    <std-sheet-layout id="root" slot="main-content" role="grid" aria-label="Time-based spreadsheet">
      <std-sheet-legend
        id="sheet-header-legend"
        slot="header-legend"
      ></std-sheet-legend>
      <std-sheet-legend
        id="sheet-footer-legend"
        slot="footer-legend"
      ></std-sheet-legend>
      <std-col id="legend" slot="legend">
        <std-sheet-legend id="sheet-legend"></std-sheet-legend>
        <std-spacer id="spacer"></std-spacer>
      </std-col>
    </std-sheet-layout>
    <div id="navigation-loading-overlay"></div>
    <div id="aria-live-region" aria-live="polite" aria-atomic="true" class="sr-only"></div>
  `;
}

/**
 * Types and values that go with `SheetElement`: `ContentVisibility`,
 * `LayoutDebugOverlayHandle`, `LegendHandle`, `LegendMeasureComparison`,
 * `NavigationUiState`, `topContentPaddingPx`, and 12 more.
 */
export namespace SheetElement {
  /** How a "ready" was reached (`Reveal.how`): the live reveal predicate passed for the current generation ("verified"),
   * or a stall/boot fail-open showed whatever was bound ("recovered"); only "verified" is a successful render. */
  export type RevealHow = "verified" | "recovered";
  /** One "ready" presentation (`onReveal`). */
  export type Reveal = {
    readonly how: RevealHow;
    /** The surface bound when it was revealed (null: the empty state). */
    readonly sheet: Option<SheetViewModel.Surface<SheetBinder>>;
    readonly foreground: boolean;
  };
  /** `bindContentVisibility`'s argument: the content and the header shadow, each shown or hidden. */
  export type ContentVisibility = {
    readonly content: "shown" | "hidden";
    readonly headerShadow: "shown" | "hidden";
  };

  /** `Sheet.layoutDebugOverlayHandle`: the layout debug overlay's switch. */
  export interface LayoutDebugOverlayHandle {
    setEnabled(enabled: boolean): void;
    clear(): void;
    rebind(): void;
  }

  /** `Sheet.legendHandle`: the legend column's resize and height rebind (sheet-legend-hosting.ts). */
  export type LegendHandle = LegendHosting.LegendHandle;

  /** A legend width measurement comparison (sheet-legend-hosting.ts). */
  export type LegendMeasureComparison = LegendHosting.LegendMeasureComparison;

  /** A navigation's UI state (sheet-element-presentation.ts). */
  export type NavigationUiState = Presentation.NavigationUiState;

  export const topContentPaddingPx: number = SheetCellTargets.topContentPaddingPx;

  /** The sheet's vertical layout arithmetic (sheet-element-layout.ts). */
  export const Layout: {
    bodyMarginTop(sheet: SheetViewModel.Surface<SheetBinder>): number;
    bodyFixedChildTop(offsetPx: number, location: string, sheet: SheetViewModel.Surface<SheetBinder>): number;
    headerHeight(sheet: SheetViewModel.Surface<SheetBinder>): number;
    legendStickyTop(pane: "frozen" | "scrolling", sheet: SheetViewModel.Surface<SheetBinder>): string;
    bottomOffset(): number;
  } = SheetElementLayout;

  export type CellPosition = SheetCellTargets.CellPosition;

  export type RebindHandle = SheetDelegate.RebindHandle;
  export type PopoverHandle = SheetDelegate.PopoverHandle;
  export type FixedChildHandle = SheetDelegate.FixedChildHandle;
  export type TooltipHandle = SheetDelegate.TooltipHandle;
  export type StaticChildHandle = SheetDelegate.StaticChildHandle;
  export type Handle = SheetDelegate;

  export type Width = SheetLayout.Width;
  export const Width: typeof SheetLayout.Width = SheetLayout.Width;

  export type WidthConstraints = SheetLayoutWidth.Constraints;

  export type Binder = SheetBinder;
  export type IndexRenderer = SheetIndexRenderer;
  export const IndexRenderer: typeof SheetIndexRenderer = SheetIndexRenderer;
}
